import { useEffect, useState, useRef } from 'react';
import { devLog } from './utils/devLog';
import { tokenService } from './services/tokenService';

const AuthCallback = () => {
    const [status, setStatus] = useState('Processing...');
    const hasRun = useRef(false);
    useEffect(() => {
        if (hasRun.current) return;
        hasRun.current = true;

        let isMounted = true;

        const cleanupAuthState = () => {
            localStorage.removeItem('code_verifier');
            localStorage.removeItem('auto_login_in_progress');
        };

        const handleCallback = async () => {
            const params = new URLSearchParams(window.location.search);
            const code = params.get('code');
            const error = params.get('error');
            const errorDescription = params.get('error_description');
            const codeVerifier = localStorage.getItem('code_verifier');

            devLog('AuthCallback - code:', code);
            devLog('AuthCallback - codeVerifier:', codeVerifier);

            // Check for OAuth error response from SSO
            if (error) {
                console.error('OAuth error:', error, errorDescription);
                cleanupAuthState();
                if (isMounted) setStatus(`Anmeldefehler: ${errorDescription || error}`);
                setTimeout(() => { window.location.replace('/'); }, 3000);
                return;
            }

            if (!code) {
                console.error('No code in callback');
                cleanupAuthState();
                if (isMounted) setStatus('Error: No authorization code received');
                setTimeout(() => { window.location.replace('/'); }, 2000);
                return;
            }

            if (!codeVerifier) {
                // Issue #297: kein Verifier in localStorage — typisch wenn der
                // OAuth-Flow auf einem anderen Origin gestartet wurde (z.B.
                // Akademie-Landingpage → SSO → Admin), oder durch Browser-
                // History rueckwaerts navigiert. Statt 2 s "Fehlermeldung" zu
                // zeigen sofort einen sauberen Re-Login starten — der greift
                // dann durch (autoLogin auf /-Route triggert checkAuth →
                // startOAuthFlow → SSO erkennt Session → Code → Verifier
                // diesmal lokal vorhanden). Walter sah genau das Symptom als
                // "erst Fehlermeldung, beim zweiten Anlauf direkt drin".
                console.warn('No code verifier — restarting OAuth flow silently');
                cleanupAuthState();
                window.location.replace('/');
                return;
            }

            try {
                devLog('Exchanging code for token...');
                if (isMounted) setStatus('Exchanging authorization code for token...');

                const ssoUrl = process.env.REACT_APP_SSO_URL || 'https://oa-sso.wsk.gmbh';
                const clientId = process.env.REACT_APP_OAUTH_CLIENT_ID || 'swagger-client';

                const response = await fetch(`${ssoUrl}/token`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: new URLSearchParams({
                        grant_type: 'authorization_code',
                        code,
                        redirect_uri: window.location.origin + '/auth-callback',
                        client_id: clientId,
                        code_verifier: codeVerifier,
                    }),
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    console.error('Token exchange failed with status:', response.status, errorData);
                    cleanupAuthState();
                    if (isMounted) setStatus(`Anmeldefehler: ${errorData.error_description || errorData.message || 'Token exchange failed'}`);
                    setTimeout(() => { window.location.replace('/'); }, 3000);
                    return;
                }

                const data = await response.json();
                devLog('Token response received');

                if (data.access_token) {
                    devLog('Token received, saving to localStorage');
                    tokenService.setToken(data.access_token);
                    // Issue #297: Refresh-Token mit speichern, damit bei
                    // 401 still erneuert werden kann statt Auto-Logout
                    // beim Token-Ablauf (Walters Panel-Wechsel-Symptom).
                    if (data.refresh_token) {
                        tokenService.setRefreshToken(data.refresh_token);
                    }
                    // Clear all auth flags - user is now logged in
                    cleanupAuthState();
                    localStorage.removeItem('user_logged_out');

                    if (isMounted) setStatus('Login successful! Redirecting...');

                    const storedRedirect = localStorage.getItem('redirect_after_login');
                    const redirectPath =
                        storedRedirect !== null && /^\/(?!\/)/.test(storedRedirect)
                            ? storedRedirect
                            : '/';
                    localStorage.removeItem('redirect_after_login');

                    devLog('Redirecting to:', redirectPath);

                    setTimeout(() => {
                        window.location.replace(redirectPath);
                    }, 500);
                } else {
                    console.error('No access_token in response:', data);
                    cleanupAuthState();
                    if (isMounted) setStatus('Error: No access token received');
                    setTimeout(() => { window.location.replace('/'); }, 2000);
                }
            } catch (error) {
                console.error('Token exchange failed:', error);
                cleanupAuthState();
                if (isMounted) setStatus('Error: Token exchange failed');
                setTimeout(() => { window.location.replace('/'); }, 2000);
            }
        };

        handleCallback();

        return () => {
            isMounted = false;
        };
    }, []);

    return (
        <div style={{
            padding: '40px',
            textAlign: 'center',
            fontFamily: 'Arial, sans-serif'
        }}>
            <h2>Authentication</h2>
            <p>{status}</p>
            <div style={{
                margin: '20px auto',
                width: '50px',
                height: '50px',
                border: '5px solid #f3f3f3',
                borderTop: '5px solid #3498db',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
            }}></div>
            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default AuthCallback;